<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1 class="font-weight-thin" v-if="packId">Edit Pack</h1>
        <h1 class="font-weight-thin" v-else>Create New Pack</h1>
      </v-col>
    </v-row>
    <v-card class="elevation-0" :loading="loading">
      <v-form ref="form" v-model="valid" v-show="!loading">
        <v-card-text class="px-0">
          <v-row>
            <v-col sm="12" md="4" lg="4">
              <v-card outlined>
                <v-card-title>
                  <span class="headline">Pack Details</span>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="py-8">
                  <v-text-field
                    :rules="nameRules"
                    clearable
                    clearIcon="fa fa-times"
                    data-cy="packForm__packTitle"
                    data-test="packTitle"
                    label="Title *"
                    outlined
                    required
                    v-model="packRecord.title"
                  ></v-text-field>

                  <v-autocomplete
                    :items="dropdowns.packStatus"
                    :rules="packStatusRules"
                    data-cy="packForm__packStatusSelect"
                    data-test="packStatus"
                    label="Pack Status *"
                    outlined
                    required
                    v-model="packRecord.status"
                  ></v-autocomplete>

                  <v-select
                    :items="dropdowns.packType"
                    :rules="packTypeRules"
                    data-cy="packForm__packTypeSelect"
                    data-test="packType"
                    item-text="name"
                    item-value="id"
                    label="Pack Type *"
                    outlined
                    required
                    v-model="packRecord.type"
                  >
                    <template v-slot:item="{ item }" data-cy="index">
                      <div data-cy="packForm__packTypeSelect--option">{{ item }}</div>
                    </template>
                  </v-select>

                  <v-text-field
                    label="Parent JIRA"
                    clearable
                    clearIcon="fa fa-times"
                    data-cy="packForm__parentJira"
                    data-test="parentJira"
                    outlined
                    v-model="packRecord.parentJira"
                  ></v-text-field>

                  <v-text-field
                    :hint="packVersionHint"
                    :label="packVersionLabel"
                    :persistent-hint="true"
                    :rules="packVersionRules"
                    class="mb-4"
                    clearable
                    clearIcon="fa fa-times"
                    data-cy="packForm__packVersion"
                    data-test="packVersion"
                    outlined
                    v-model="packRecord.packVersion"
                  ></v-text-field>

                  <user-picker
                    attributeName="packLeadSelect"
                    :isRequired="true"
                    :outlined="true"
                    fieldName="Pack Lead"
                    clearIcon="fa fa-times"
                    data-test="packLead"
                    label="Pack Lead"
                    v-model="packRecord.packLead"
                  ></user-picker>

                  <user-picker
                    attributeName="projectManagerSelect"
                    :isRequired="isEnhancementPack"
                    :outlined="true"
                    clearIcon="fa fa-times"
                    data-test="projectManager"
                    label="Project Manager"
                    fieldName="Project Manager"
                    v-model="packRecord.projectManager"
                  ></user-picker>

                  <v-autocomplete
                    :item-text="serviceAreaDisplayText"
                    :items="serviceAreaList"
                    :label="pilotParticipationLabelText"
                    :placeholder="placeholderText"
                    :rules="pilotParticipationRules"
                    chips
                    class="mt-7 mb-1"
                    clearable
                    clearIcon="fa fa-times"
                    data-cy="packForm__serviceAreaList"
                    deletable-chips
                    item-value="id"
                    multiple
                    outlined
                    required
                    small
                    v-model="pilotParticipationList"
                  ></v-autocomplete>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col sm="12" md="6" lg="6">
              <v-card outlined>
                <v-card-title>
                  <span class="headline">Dates</span>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-9 pb-12">
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="buildDatesAreRequired"
                        :value="packRecord.dateBuildStart"
                        @selectedDate="selectBuildStartDate"
                        attributeName="dateBuildStart"
                        clearIcon="fa fa-times"
                        dense
                        label="Build Start Date"
                      >
                      </date-picker>
                    </v-col>

                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="buildDatesAreRequired"
                        :value="packRecord.dateBuildEnd"
                        @selectedDate="select('dateBuildEnd', $event)"
                        :minDate="minBuildEndDate"
                        attributeName="dateBuildEnd"
                        clearIcon="fa fa-times"
                        dense
                        label="Build End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEnhancementPack">
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateWorkflowStart"
                        @selectedDate="selectWorkflowStartDate"
                        attributeName="dateWorkflowStart"
                        clearIcon="fa fa-times"
                        dense
                        label="Workflow Start Date"
                      >
                      </date-picker>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateWorkflowEnd"
                        @selectedDate="select('dateWorkflowEnd', $event)"
                        :minDate="minWorkflowEndDate"
                        attributeName="dateWorkflowEnd"
                        clearIcon="fa fa-times"
                        dense
                        label="Workflow End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="true"
                        :value="packRecord.dateQaStart"
                        @selectedDate="selectQaStartDate"
                        attributeName="dateQaStart"
                        clearIcon="fa fa-times"
                        dense
                        label="QA Start Date"
                      >
                      </date-picker>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="true"
                        :value="packRecord.dateQaEnd"
                        @selectedDate="select('dateQaEnd', $event)"
                        :minDate="minQaEndDate"
                        attributeName="dateQaEnd"
                        clearIcon="fa fa-times"
                        dense
                        label="QA End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="uatDatesAreRequired"
                        :value="packRecord.uatStart"
                        @selectedDate="selectUatStart"
                        attributeName="uatStart"
                        clearIcon="fa fa-times"
                        data-test="uatStart"
                        dense
                        label="UAT Start Date"
                      >
                      </date-picker>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="uatDatesAreRequired"
                        :value="packRecord.uatEnd"
                        @selectedDate="selectUatEnd"
                        :minDate="minUatEndDate"
                        attributeName="uatEnd"
                        clearIcon="fa fa-times"
                        data-test="uatEnd"
                        dense
                        label="UAT End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEnhancementPack">
                    <v-col cols="12" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateEndUserDocumentation"
                        @selectedDate="select('dateEndUserDocumentation', $event)"
                        attributeName="dateEndUserDocumentation"
                        clearIcon="fa fa-times"
                        dense
                        label="End User Documentation Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEnhancementPack">
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateOperationalizeStart"
                        @selectedDate="selectOperationalizeStartDate"
                        attributeName="dateOperationalizeStart"
                        clearIcon="fa fa-times"
                        dense
                        label="Operationalize Start Date"
                      >
                      </date-picker>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateOperationalizeEnd"
                        @selectedDate="select('dateOperationalizeEnd', $event)"
                        :minDate="minOperationalizeEndDate"
                        attributeName="dateOperationalizeEnd"
                        clearIcon="fa fa-times"
                        dense
                        label="Operationalize End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>

                  <v-row v-if="isEnhancementPack">
                    <v-col cols="12" class="py-0">
                      <date-picker
                        :isRequired="isEnhancementPack"
                        :value="packRecord.dateEpDemo"
                        @selectedDate="select('dateEpDemo', $event)"
                        attributeName="dateEpDemo"
                        clearIcon="fa fa-times"
                        dense
                        label="EP Demo Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" class="py-0">
                      <date-picker
                        :isRequired="true"
                        :value="packRecord.productionDate"
                        @selectedDate="select('productionDate', $event)"
                        attributeName="productionDate"
                        clearIcon="fa fa-times"
                        dense
                        label="Production Implementation Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="stabilizationDatesAreRequired"
                        :value="packRecord.dateStabilizationStart"
                        @selectedDate="selectStabilizationStartDate"
                        attributeName="dateStabilizationStart"
                        clearIcon="fa fa-times"
                        dense
                        label="Stabilization Start Date"
                      >
                      </date-picker>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <date-picker
                        :isRequired="stabilizationDatesAreRequired"
                        :value="packRecord.dateStabilizationEnd"
                        @selectedDate="select('dateStabilizationEnd', $event)"
                        :minDate="minStabilizationEndDate"
                        attributeName="dateStabilizationEnd"
                        clearIcon="fa fa-times"
                        dense
                        label="Stabilization End Date"
                      >
                      </date-picker>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn data-cy="packForm__cancelButton" @click="cancel" outlined>Cancel</v-btn>
          <v-btn elevation="0" data-cy="packForm__saveButton" @click="save" :disabled="!valid" color="primary"
            >Save Pack</v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";

import DatePicker from "@/components/datePicker";
import UserPicker from "@/components/userPicker";

export default {
  name: "packForm",
  components: {
    DatePicker,
    UserPicker,
  },
  computed: {
    buildDatesAreRequired() {
      return !this.isCriticalFixPack;
    },
    packVersionRules() {
      const caseSensitivityHint = v => {
        if (!v) return true;

        v = v.trim();

        // Check fixed strings
        const exactStrings = ["Standard", "Critical Fix", "Non-aligned", "FYI Reference Change"];
        const lowerInput = v.toLowerCase();

        for (const exactString of exactStrings) {
          if (lowerInput === exactString.toLowerCase() && v !== exactString) {
            return `Must match case exactly: '${exactString}'`;
          }
        }

        // Check monthly format (case-insensitive)
        const monthlyPattern = /^(january|february|march|april|may|june|july|august|september|october|november|december) (202\d) (ep|sp|fp)$/i;
        const match = v.match(monthlyPattern);

        if (match) {
          const month = match[1].toLowerCase();
          const year = match[2];
          const packType = match[3].toUpperCase();

          // Capitalize month and uppercase pack type
          const correctFormat = `${month.charAt(0).toUpperCase() + month.slice(1)} ${year} ${packType}`;

          if (v !== correctFormat) {
            return `Must match case exactly: '${correctFormat}'`;
          }
        }

        return true;
      };

      const formatRule = v => {
        if (!this.packVersionIsRequired && !v) return true;

        const regex = /^((January|February|March|April|May|June|July|August|September|October|November|December) 202\d (EP|SP|FP)|Standard|Critical Fix|Non-aligned|FYI Reference Change)$/;
        return (
          regex.test(v && v.trim()) ||
          "Pack Version must be in the format of 'Month Year EP/SP/FP', 'Standard', 'Critical Fix', 'Non-aligned', or 'FYI Reference Change'."
        );
      };

      if (this.packVersionIsRequired) {
        const rule = v => !!v || "Pack Version is required when the Pack Type is Client or Enhancement.";
        return [rule, caseSensitivityHint, formatRule];
      }

      return [caseSensitivityHint, formatRule];
    },
    packVersionIsRequired() {
      return this.isEnhancementPack;
    },

    packVersionHint() {
      return 'Accepted formats: "Month Year EP/SP/FP" (e.g., January 2025 EP; year 2020-2029), "Standard", "Critical Fix", "Non-aligned", or "FYI Reference Change"';
    },
    packVersionLabel() {
      return this.packVersionIsRequired ? "Pack Version *" : "Pack Version";
    },
    isCriticalFixPack() {
      return this.packRecord.type === "Critical Fix";
    },
    isEnhancementPack() {
      return this.packRecord.type === "Enhancement Pack";
    },
    isProjectPack() {
      return this.packRecord.type === "Project";
    },
    isServicePack() {
      return this.packRecord.type === "Service Pack";
    },
    packId() {
      return parseInt(this.$route.params.id, 10);
    },
    pilotParticipationList: {
      get() {
        return this.$store.state.pack.packRecord.pilotParticipationList;
      },
      set(val) {
        this.$store.commit("pack/setPilotParticipationList", val);
      },
    },
    pilotParticipationRules() {
      const rules = [];
      if (this.packRecord.type === "Project") {
        const rule = list => !!list.length || "Pilot Participation is required when the Pack Type is Project.";
        rules.push(rule);
      }

      return rules;
    },
    pilotParticipationLabelText() {
      if (this.packRecord.type === "Project") {
        return "Pilot Participation*";
      }
      return "Pilot Participation";
    },
    placeholderText() {
      if (this.packRecord.type === "Project") {
        return;
      }
      return "Unspecified";
    },
    stabilizationDatesAreRequired() {
      return this.isEnhancementPack || this.packRecord.type === "Service";
    },
    uatDatesAreRequired() {
      return !this.isCriticalFixPack;
    },
    ...mapState({
      dropdowns: state => state.dropdown.dropdowns,
      pack: state => state.pack.pack,
      packRecord: state => state.pack.packRecord,
      serviceAreaList: state => state.admin.serviceArea.list,
      userList: state => state.user.userList,
    }),
  },
  async created() {
    this.loading = true;
    try {
      await Promise.all[(this.$store.dispatch("user/load"), this.$store.dispatch("admin/serviceArea/fetchList"))];
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "There was an error loading pack data." });
      this.loading = false;
    }
    if (this.packId) {
      await this.fetchPack(this.packId);
    } else {
      this.$store.dispatch("pack/resetPackRecord");
      this.loading = false;
    }

    this.pilotParticipationList = this.packRecord.pilotParticipationList;
    this.loading = false;
  },
  data() {
    return {
      currentDate: new Date(),
      loading: false,
      minBuildEndDate: "2000-01-01",
      minOperationalizeEndDate: "2000-01-01",
      minQaEndDate: "2000-01-01",
      minStabilizationEndDate: "2000-01-01",
      minUatEndDate: "2000-01-01",
      minWorkflowEndDate: "2000-01-01",
      nameRules: [name => !!(name && name.trim()) || "Name is required"],
      packStatusRules: [packStatus => !!packStatus || "Pack Status is required"],
      packTypeRules: [packType => !!packType || "Pack Type is required"],
      valid: true,
    };
  },
  methods: {
    async refreshPackList() {
      try {
        await this.fetchPackList();
      } catch (e) {
        console.error("Unable to refresh pack list", e);
        this.$notifyError({ message: "Unable to refresh pack list." });
      }
    },
    async save() {
      if (typeof this.packRecord.title === "string") {
        this.packRecord.title = this.packRecord.title.trim();
      }
      if (typeof this.packRecord.packVersion === "string") {
        this.packRecord.packVersion = this.packRecord.packVersion.trim();
      }

      let dispatchAction = "pack/create";
      let packIdForRedirect = this.packId;

      if (this.packId) {
        dispatchAction = "pack/update";
      }

      try {
        await this.$store.dispatch(dispatchAction);
        this.$notifySuccess({ message: "Successfully saved pack data" });

        if (!packIdForRedirect) {
          packIdForRedirect = this.pack.id;
        }
        this.$router.push({ name: "adminPackView", params: { id: packIdForRedirect } });
      } catch (e) {
        console.error("Pack record did not save", e);
        this.$notifyError({ message: "There was an error saving pack data" });
      }
      this.refreshPackList();
    },
    selectBuildStartDate(val) {
      this.packRecord.dateBuildStart = val;
      this.minBuildEndDate = val;
    },
    select(attribute, val) {
      this.packRecord[attribute] = val;
    },
    selectOperationalizeStartDate(val) {
      this.packRecord.dateOperationalizeStart = val;
      this.minOperationalizeEndDate = val;
    },
    selectQaStartDate(val) {
      this.packRecord.dateQaStart = val;
      this.minQaEndDate = val;
    },
    selectStabilizationStartDate(val) {
      this.packRecord.dateStabilizationStart = val;
      this.minStabilizationEndDate = val;
    },
    selectUatStart(val) {
      this.packRecord.uatStart = val;
      this.minUatEndDate = val;
    },
    selectUatEnd(val) {
      this.packRecord.uatEnd = val;
    },
    selectWorkflowStartDate(val) {
      this.packRecord.dateWorkflowStart = val;
      this.minWorkflowEndDate = val;
    },
    serviceAreaDisplayText(item) {
      if (item) {
        return `${item.id} - ${item.name}`;
      }
    },
    cancel() {
      if (this.packId) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: "adminPackList" });
      }
      this.$store.dispatch("pack/resetPackRecord");
    },
    ...mapActions({
      fetchPack: "pack/setEditPack",
      fetchPackList: "pack/getList",
    }),
  },
  watch: {
    "packRecord.type"() {
      if (this.$refs) {
        this.$nextTick(() => {
          this.$refs.form.resetValidation();
        });
      }
    },
  },
};
</script>

<style scoped>
.fieldset {
  border: 1px solid #ccc;
  padding: 16px;
  margin-bottom: 10px;
}

.legend {
  font-size: 1.6em;
  padding: 10px;
}
.v-messages__message {
  line-height: 1.3 !important;
}
</style>
